import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Compass, X, Save, Loader2, LocateFixed, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const R = 6371000;
const rad = (d) => (d * Math.PI) / 180;
function bearing(a, b) {
  const y = Math.sin(rad(b.lng - a.lng)) * Math.cos(rad(b.lat));
  const x = Math.cos(rad(a.lat)) * Math.sin(rad(b.lat)) - Math.sin(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.cos(rad(b.lng - a.lng));
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}
function dist(a, b) {
  const h = Math.sin(rad(b.lat - a.lat) / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(rad(b.lng - a.lng) / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

// Kompas telefonu: iOS webkitCompassHeading, Android deviceorientationabsolute (alpha liczona przeciwnie do wskazówek).
function useHeading() {
  const [heading, setHeading] = useState(null);
  const [pitch, setPitch] = useState(null);
  const [needPerm, setNeedPerm] = useState(typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function");
  useEffect(() => {
    if (needPerm) return undefined;
    const onOri = (e) => {
      let h = null;
      if (typeof e.webkitCompassHeading === "number") h = e.webkitCompassHeading;
      else if (e.absolute && typeof e.alpha === "number") h = (360 - e.alpha) % 360;
      if (h != null) {
        const so = (window.screen?.orientation?.angle || 0);
        setHeading((h + so) % 360);
      }
      if (typeof e.beta === "number") setPitch(e.beta - 90);
    };
    const evt = "ondeviceorientationabsolute" in window ? "deviceorientationabsolute" : "deviceorientation";
    window.addEventListener(evt, onOri, true);
    return () => window.removeEventListener(evt, onOri, true);
  }, [needPerm]);
  const ask = async () => {
    try { const r = await DeviceOrientationEvent.requestPermission(); if (r === "granted") setNeedPerm(false); else toast.error("Brak zgody na kompas"); }
    catch { toast.error("Kompas niedostępny"); }
  };
  return { heading, pitch, needPerm, ask };
}

function Arrow({ rot, ok }) {
  return (
    <svg viewBox="0 0 200 200" className="w-56 h-56" data-testid="aim-arrow">
      <circle cx="100" cy="100" r="92" fill="none" stroke="#334155" strokeWidth="4" />
      {[0, 90, 180, 270].map((a) => <line key={a} x1="100" y1="10" x2="100" y2="22" stroke="#64748b" strokeWidth="3" transform={`rotate(${a} 100 100)`} />)}
      <g transform={`rotate(${rot} 100 100)`} style={{ transition: "transform 0.15s linear" }}>
        <polygon points="100,18 124,110 100,94 76,110" fill={ok ? "#22C55E" : "#06B6D4"} />
        <polygon points="100,182 116,118 100,128 84,118" fill="#475569" />
      </g>
    </svg>
  );
}

export default function AntennaAim({ installationId, onClose }) {
  const [pos, setPos] = useState(null);
  const [aim, setAim] = useState(null);
  const [err, setErr] = useState("");
  const [m, setM] = useState({ rssi: "", snr: "", ccq: "", tx_rate: "", rx_rate: "" });
  const [busy, setBusy] = useState(false);
  const lastFetch = useRef(0);
  const { heading, pitch, needPerm, ask } = useHeading();

  useEffect(() => {
    api.get(`/wifi/installations/${installationId}/aim`).then((r) => setAim((prev) => (prev?.source === "gps" ? prev : r.data))).catch((e) => setErr(formatApiError(e.response?.data?.detail)));
    if (!navigator.geolocation) { setErr("Telefon nie udostępnia GPS"); return undefined; }
    const id = navigator.geolocation.watchPosition(
      (p) => setPos({ lat: p.coords.latitude, lng: p.coords.longitude, alt: p.coords.altitude, acc: p.coords.accuracy }),
      () => setErr("Brak dostępu do GPS — zezwól na lokalizację"), { enableHighAccuracy: true, maximumAge: 2000, timeout: 20000 });
    return () => navigator.geolocation.clearWatch(id);
  }, [installationId]);

  useEffect(() => {
    if (!pos || Date.now() - lastFetch.current < 5000) return;
    lastFetch.current = Date.now();
    const q = `lat=${pos.lat}&lng=${pos.lng}${pos.alt != null ? `&alt=${pos.alt}` : ""}`;
    api.get(`/wifi/installations/${installationId}/aim?${q}`).then((r) => setAim(r.data)).catch(() => {});
  }, [pos, installationId]);

  const t = aim?.target;
  const live = pos && t?.lat != null;
  const az = live ? bearing(pos, t) : aim?.azimuth;
  const d = live ? dist(pos, t) : aim?.distance_m;
  const diff = heading != null && az != null ? ((az - heading + 540) % 360) - 180 : null;
  const ok = diff != null && Math.abs(diff) <= 3;
  const elev = aim?.elevation_deg;

  const save = async (approve) => {
    setBusy(true);
    try {
      const body = Object.fromEntries(Object.entries(m).filter(([, v]) => v !== ""));
      if (heading != null) body.azimuth_set = Math.round(heading * 10) / 10;
      if (pos) { body.lat = pos.lat; body.lng = pos.lng; }
      await api.post(`/wifi/installations/${installationId}/measurements`, { ...body, approve });
      toast.success(approve ? "Instalacja zatwierdzona" : "Zapisano pomiary"); if (approve) onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[5000] bg-slate-950 text-white overflow-y-auto" data-testid="antenna-aim">
      <div className="flex items-center justify-between p-4 border-b border-slate-800">
        <div className="flex items-center gap-2 font-semibold"><Compass size={20} className="text-cyan-400" /> Ustawienie anteny</div>
        <button onClick={onClose} data-testid="aim-close"><X size={22} /></button>
      </div>
      <div className="p-4 space-y-4">
        {err && <div className="bg-red-950/50 border border-red-800 rounded-lg p-3 text-sm" data-testid="aim-error">{err}</div>}
        {aim && (
          <div className="text-sm text-slate-300">
            <div><b>{aim.installation?.client_name}</b> · {aim.installation?.address || ""}</div>
            <div>Cel: <b>{t?.site || "—"}</b>{t?.sector ? ` · sektor ${t.sector}` : ""}{t?.freq_mhz ? ` · ${t.freq_mhz} MHz` : ""}{t?.channel ? ` · kanał ${t.channel}` : ""}</div>
          </div>
        )}
        <div className="flex flex-col items-center">
          <Arrow rot={diff ?? 0} ok={ok} />
          {needPerm && <button onClick={ask} data-testid="aim-compass-permission" className="mt-2 bg-cyan-600 rounded-lg px-4 py-2 text-sm">Włącz kompas</button>}
          <div className={`mt-2 text-lg font-bold ${ok ? "text-emerald-400" : "text-slate-200"}`} data-testid="aim-instruction">
            {diff == null ? (heading == null ? "Czekam na kompas telefonu…" : "Czekam na GPS…")
              : ok ? <span className="flex items-center gap-1"><CheckCircle2 size={20} /> Kierunek OK</span>
                : diff > 0 ? `Obróć w prawo o ${Math.round(diff)}°` : `Obróć w lewo o ${Math.round(-diff)}°`}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 text-center">
          <div className="bg-slate-900 rounded-lg p-3"><div className="text-[11px] text-slate-400">Azymut do bazy</div><div className="text-2xl font-mono" data-testid="aim-azimuth">{az != null ? `${az.toFixed(1)}°` : "—"}</div></div>
          <div className="bg-slate-900 rounded-lg p-3"><div className="text-[11px] text-slate-400">Kompas telefonu</div><div className="text-2xl font-mono" data-testid="aim-heading">{heading != null ? `${Math.round(heading) % 360}°` : "—"}</div></div>
          <div className="bg-slate-900 rounded-lg p-3"><div className="text-[11px] text-slate-400">Kąt pionowy (elewacja)</div><div className="text-2xl font-mono" data-testid="aim-elevation">{elev != null ? `${elev > 0 ? "+" : ""}${elev}°` : "—"}</div>
            {pitch != null && <div className="text-[10px] text-slate-500">nachylenie telefonu {pitch.toFixed(0)}°</div>}</div>
          <div className="bg-slate-900 rounded-lg p-3"><div className="text-[11px] text-slate-400">Odległość</div><div className="text-2xl font-mono" data-testid="aim-distance">{d != null ? (d >= 1000 ? `${(d / 1000).toFixed(2)} km` : `${Math.round(d)} m`) : "—"}</div></div>
        </div>
        <div className="text-[11px] text-slate-500 flex items-start gap-1">
          <LocateFixed size={12} className="mt-0.5 shrink-0" />
          {pos ? `GPS ±${Math.round(pos.acc)} m · ${aim?.source === "gps" ? "kierunek z GPS telefonu" : "kierunek z pozycji w systemie"}` : "Kierunek z pozycji zapisanej w systemie (czekam na GPS telefonu)"}.
          {" "}Trzymaj telefon płasko, z dala od metalu i masztu — kompas może się mylić o kilka stopni. Ustawienie końcowe wg RSSI/SNR. {aim?.estimated_note}
        </div>
        {aim?.in_sector === false && <div className="text-amber-300 text-sm" data-testid="aim-out-of-sector">Uwaga: ta lokalizacja jest poza zasięgiem/szerokością wiązki sektora.</div>}
        <div className="bg-slate-900 rounded-lg p-3 space-y-2">
          <div className="text-sm font-semibold">Pomiary po ustawieniu</div>
          <div className="grid grid-cols-2 gap-2">
            {[["rssi", "RSSI [dBm]"], ["snr", "SNR [dB]"], ["ccq", "CCQ [%]"], ["tx_rate", "TX [Mb/s]"], ["rx_rate", "RX [Mb/s]"]].map(([k, l]) => (
              <label key={k} className="text-[11px] text-slate-400">{l}
                <input inputMode="decimal" value={m[k]} onChange={(e) => setM({ ...m, [k]: e.target.value })} data-testid={`aim-m-${k}`}
                  className="w-full bg-slate-800 border border-slate-700 rounded px-2 py-2 text-sm text-white" /></label>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2 pt-1">
            <button onClick={() => save(false)} disabled={busy} data-testid="aim-save" className="bg-slate-700 rounded-lg py-2.5 text-sm flex items-center justify-center gap-1">{busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Zapisz</button>
            <button onClick={() => save(true)} disabled={busy} data-testid="aim-approve" className="bg-emerald-600 rounded-lg py-2.5 text-sm flex items-center justify-center gap-1"><CheckCircle2 size={15} /> Zatwierdź instalację</button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
